import { graphql } from "@apollo/client/react/hoc";
import * as React from "react";
import { RouteComponentProps, Switch, Route, Redirect, matchPath } from "react-router-dom";
import { build, Scope } from "bombon";
import produce from "immer";
import _, { flowRight as compose } from "lodash";
import moment from "moment-timezone";
import cx from "classnames";
import { option, readonlyRecord as record, function as fn } from "fp-ts";
import useRouter from "use-react-router";

import { Gig, CheckInOutStatus, SkipReportingSteps } from "gql-gen";
import clipboardIcon from "assets/clipboard-list-check-solid.svg";

import Icon from "components/Icon";
import Text from "components/LegacyText";
import ListView from "components/ListView";
import LoadingContainer from "components/LoadingContainer";
import { GigReportStep, GigReportAnswer } from "interfaces/ReportStep";
import { getJwt } from "utilities/authentication";
import { FINAL_REPORT_STATES } from "interfaces/Gig";
import { StorageScope } from "utilities/storageScope";
import Button from "components/Button";
import ActionFooter from "components/ActionFooter";
import FooterDistance from "components/FooterDistance";
import { earthRadiusMi, getDistance } from "utilities/getPosition";
import { formatDistance } from "utilities/string";
import { Desktop } from "components/Layout/Responsive";
import { AppName, getAppUrl, getBaseAppUrl } from "utilities/routes";
import { bombonNodeTypes } from "modules/Report/StepForm/bombonNodeTypes";
import { isPinata } from "utilities/flavor";
import { getRustBackendUrl } from "utilities/getServerRootUrl";
import ReportHeader from "modules/Gogetter/Header";
import { UserInfo } from "modules/Dashboard/UserInfo";

import styles from "./styles.scss";

import saveStep from "./saveStep.gql";
import reportSteps from "./reportSteps.gql";
import submitReport from "./submitReport.gql";

import { ggStorage } from "../Gogetter/storage";
import StepItem from "./StepItem";
import StepForm, { BombonQuestion, getStateId } from "./StepForm";
import { ReportQuestionWChildren, StepState } from "./StepState";
import CheckInOut from "./CheckInOut";
import Expenses from "./Expenses";
import { CONFIG } from "./config";
import { getNextStep } from "./getNextStep";

const RUSTEND_URL = `${getRustBackendUrl()}/api/v1`;

export interface Data {
  loading: boolean;
  gigReportSteps: GigReportStep[];
  refetch: () => Promise<void>;
}

interface ParentProps {
  gig: Gig;
  gigRefetch: () => Promise<void>;
  userInfo: UserInfo;
  verifyReport?: boolean;
  onSaveAndClose?: () => void;
  agencyName?: string;
  clientName?: string;
  logoUrl?: string;
}

interface HoCProps {
  data: Data;
  saveStep: (param0: {}) => Promise<{}>;
  submitReport: (param0: {}) => Promise<{}>;
}

interface StepStates {
  [stepKey: string]: StepState;
}

interface Conditionals {
  [qId: string]: any;
}

interface State {
  steps: StepStates | null;
  anyError: boolean;
  submitState: null | "submitting" | "error";
  saveState: null | "saving" | "error";
  conditionals: Conditionals;
  expensesDirty: boolean;
  homeAppUrl: string;
}

const reportStorage = ggStorage.subScope<StepStates>("reports");

type Props = ParentProps & HoCProps & RouteComponentProps;

class Report extends React.Component<Props, State> {
  readonly state: State = {
    steps: null,
    anyError: false,
    saveState: null,
    submitState: null,
    conditionals: {},
    expensesDirty: false,
    homeAppUrl: "",
  };

  public componentDidMount() {
    const { match, userInfo, gig } = this.props;
    const orgId = (match.params as any).orgId;
    const reportOrgId = orgId || userInfo.me?.organizationUser?.organizationId;

    if (userInfo.me?.organizationUser?.theAppReportsEnabled && reportOrgId && gig?.id) {
      window.location.replace(getAppUrl("theApp", `${reportOrgId}/reports/${gig.id}`));
      return;
    }

    window.addEventListener("online", this.saveErrored);

    if (orgId) {
      this.fetchHomeApp(orgId);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("online", this.saveErrored);
  }

  public componentDidUpdate(_: Props, { steps: prevSteps }: State) {
    const { steps } = this.state;
    const { location, match } = this.props;

    if (steps) {
      // Wait until we have the steps from getDerivedStateFromProps
      if (!prevSteps) {
        this.saveErrored();
      }
    }
  }

  public static getDerivedStateFromProps(props: Props, { steps: currentSteps }: State) {
    const { gig, data } = props;
    if (gig && data && data.gigReportSteps) {
      const { gigReportSteps } = data;
      let steps: StepStates = {};

      let conditionals: Conditionals = {};

      for (const step of gigReportSteps) {
        for (const question of step.questions) {
          if (question.bombonAST) {
            conditionals[question.id] = build(question.bombonAST, bombonNodeTypes);
          }
        }

        const queuedChanges = Object.values(step.state)
          .map((state: GigReportAnswer<any>) => (state.value === null && state.defaultValue ? new Date() : null))
          .filter((d): d is Date => d instanceof Date);

        const updatedState = fn.pipe(
          step.state,
          record.map(answer =>
            answer.value === null && answer.defaultValue ? { ...answer, value: answer.defaultValue } : answer,
          ),
        );

        steps[step.key] = {
          queuedChanges,
          savingChanges: [],
          failedChanges: [],
          state: updatedState,
          questions: step.questions,
          ...(currentSteps?.[step.key] ?? {}),
          step,
        };
      }
      if (!currentSteps) {
        const conditionalSteps = Report.updateConditionals(props, { conditionals, steps });

        if (conditionalSteps) {
          steps = conditionalSteps;
        }

        let anyError = false;

        const unsaved = reportStorage.subScope<StepStates>(gig.id).value;

        if (unsaved && Object.keys(unsaved).length) {
          steps = { ...steps, ...unsaved };
          anyError = true;
        }

        return { steps, anyError, conditionals };
      }

      return { steps };
    }

    return null;
  }

  private static updateConditionals = (
    props: Props,
    { conditionals, steps }: Pick<State, "steps" | "conditionals">,
  ): StepStates | null => {
    if (!steps) return null;

    const {
      data: { gigReportSteps },
      gig,
    } = props;

    const globalScope = new Scope();

    const makeQ = (gigReportSteps: GigReportStep[]) => {
      const q: { [qId: string]: BombonQuestion } = {};
      const gq: { [qGroupKey: string]: BombonQuestion } = {};
      const qIndex: { [qId: string]: ReportQuestionWChildren } = {};

      for (const step of gigReportSteps) {
        for (const question of step.questions) {
          const id = question.id;
          const answer = steps[step.key].state[getStateId(id, step.productId)];
          const conditionalAnswer =
            (question.answerType === "money" || question.answerType === "money_with_negative") && answer?.value
              ? { ...answer, value: answer.value / 100 }
              : answer;

          q[question.key || id] = { id, ...conditionalAnswer };
          gq[question.groupKey] = { id, ...conditionalAnswer };
          qIndex[id] = { ...question, children: [] };
        }
      }

      return { q, gq, qIndex };
    };

    const { q: globalQ, qIndex: globalQIndex, gq: globalQByGroupKey } = makeQ(gigReportSteps.filter(s => !s.productId));

    const report = {
      gig: {
        id: gig.id,
        startTime: gig.startTime ? new Date(gig.startTime) : null,
        endTime: gig.endTime ? new Date(gig.endTime) : null,
      },

      program: {
        id: gig.program.id,
        selfService: gig.program.id,
      },

      checkedIn: gig.checkinStatus && gig.checkinStatus.time,
      checkedOut: gig.checkoutStatus && gig.checkoutStatus.time,
    };

    globalScope.set("report", report);
    globalScope.set("log", console.log);

    return gigReportSteps.reduce((newSteps, gigReportStep) => {
      const { questions, productId } = gigReportStep;
      const stepState = steps[gigReportStep.key];
      const { state } = stepState;

      let activeQuestions: ReportQuestionWChildren[] = [];

      const toMarkAsRevealed: [string, "pending" | "answered"][] = [];
      const toMarkAsUnasked: string[] = [];

      const formCondQuestions = [];
      const childrenQuestions: { [qId: string]: string[] } = {};

      const stepScope = globalScope.child("program");

      let q = globalQ;
      let gq = globalQByGroupKey;
      let qIndex = globalQIndex;

      if (productId) {
        const { q: stepQ, qIndex: stepQIndex, gq: stepQByGroupKey } = makeQ([gigReportStep]);
        q = { ...globalQ, ...stepQ };
        gq = { ...globalQByGroupKey, ...stepQByGroupKey };
        qIndex = { ...globalQIndex, ...stepQIndex };
      }

      stepScope.set("q", q);
      stepScope.set("gq", gq);

      for (const { id: questionId } of questions) {
        if (!(questionId in conditionals)) {
          continue;
        }

        let show = false;
        let under = null;

        const scope = stepScope.child("program");
        scope.set("currentStep", gigReportStep);
        scope.set("show", () => (show = true));
        scope.set("showUnder", (q: BombonQuestion) => {
          show = true;
          under = q.id;
        });
        scope.set("hide", () => {
          show = false;
          under = null;
        });

        conditionals[questionId].eval(scope);

        const stateId = getStateId(questionId, productId);

        if (show) {
          if (under) {
            childrenQuestions[under] = childrenQuestions[under] || [];
            childrenQuestions[under].push(questionId);
          } else {
            formCondQuestions.push(questionId);
          }

          if (state[stateId]?.state === "unasked") {
            // An Output (pin_output) left empty is a complete answer, the
            // engine creates a new PIN, so it is revealed already answered.
            // Otherwise the worker would have to pick and clear a PIN to submit.
            const revealed = qIndex[questionId]?.answerType === "pin_output" ? "answered" : "pending";
            toMarkAsRevealed.push([stateId, revealed]);
          }
        } else {
          if (state[stateId]?.state !== "unasked") {
            toMarkAsUnasked.push(stateId);
          }
        }
      }

      for (let question of questions) {
        if (!question.bombonAST || formCondQuestions.includes(question.id)) {
          const childrenIds = childrenQuestions[question.id];
          let qToShow: ReportQuestionWChildren = { ...question, children: [] };

          if (childrenIds) {
            for (const childId of childrenIds) {
              if (qIndex[childId]) {
                qToShow.children!.push(qIndex[childId]);
              } else {
                console.warn(`Unknown question id: ${childId}`);
              }
            }
          }

          activeQuestions.push(qToShow);
        }
      }

      const newStepState = { ...stepState, questions: activeQuestions };
      if (toMarkAsRevealed.length || toMarkAsUnasked.length) {
        for (const [qId, state] of toMarkAsRevealed) {
          newStepState.state = { ...newStepState.state, [qId]: { state, value: null } };
        }

        for (const qId of toMarkAsUnasked) {
          newStepState.state = { ...newStepState.state, [qId]: { state: "unasked", value: null } };
        }

        newStepState.queuedChanges = [...stepState.queuedChanges, new Date()];
      }

      return { ...newSteps, [gigReportStep.key]: newStepState };
    }, {});
  };

  private debouncedUpdateConditionals = _.debounce(() => {
    this.setState({
      steps: Report.updateConditionals(this.props, this.state),
    });
  }, 500);

  public saveErrored = async () => {
    const { steps, anyError } = this.state;

    if (steps && anyError) {
      this.save();
    }
  };

  public handleAnswerChanged = (stepKey: string) => (qId: string, answer: GigReportAnswer<{}>, debounce: boolean) => {
    const { gig, verifyReport } = this.props;

    const notCheckedInWithRequiredCheckin = gig?.programRole?.role?.hasCheckin && !gig?.checkinStatus;
    const isOnFinalStates = FINAL_REPORT_STATES.includes(gig?.workflowStateKey);

    const skipChange = !verifyReport && gig && (notCheckedInWithRequiredCheckin || isOnFinalStates);

    if (!CONFIG.steps.allowUpdatesBeforeCheckIn && skipChange) {
      return;
    }

    this.setState(
      state =>
        produce(state, draft => {
          if (draft.steps) {
            // A change the worker made counts as saved from here on, even
            // before the round trip: an Output's Start Selected must not
            // re-pick over their "create new" when the step remounts.
            draft.steps[stepKey].state[qId] = { ...answer, saved: true };
            draft.steps[stepKey].queuedChanges.push(new Date());

            if (!debounce) {
              draft.steps = Report.updateConditionals(this.props, draft);
            }
          }
        }),
      () => {
        if (debounce) this.debouncedUpdateConditionals();
      },
    );
  };

  reportHasUnsavedChanges = () => {
    return fn.pipe(
      option.fromNullable(this.state.steps),
      option.exists(record.some(step => step.queuedChanges.length > 0)),
    );
  };

  public getActiveStepKey() {
    const { location, match } = this.props;
    const matchResult = matchPath<{ stepKey: string }>(location.pathname, {
      path: `${match.path}/:stepKey`,
      exact: false,
      strict: false,
    });

    return matchResult?.params.stepKey;
  }

  public save = async () => {
    const {
      gig: { id },
      saveStep,
    } = this.props;

    if (!this.state.steps) {
      throw new Error("save called before steps loaded");
    }

    const updateStep = (stepKey: string, update: (current: StepState) => Partial<StepState>) => {
      this.setState(
        state => {
          if (state.steps && state.steps[stepKey]) {
            return {
              ...state,
              steps: {
                ...state.steps,
                [stepKey]: {
                  ...state.steps[stepKey],
                  ...update(state.steps[stepKey]),
                },
              },
            };
          }

          return state;
        },
        () => {
          const {
            gig: { id },
          } = this.props;
          const { steps } = this.state;

          if (steps) {
            const storage: StorageScope<StepStates> = reportStorage.subScope(id);
            const failedSteps = _.pickBy(steps, s => s.failedChanges.length > 0);

            storage.value = failedSteps;

            this.setState({
              anyError: Object.keys(failedSteps).length > 0,
            });
          }
        },
      );
    };

    this.setState({ saveState: "saving" });
    let resultingSaveState: typeof this.state.saveState = null;
    let shouldRefetch = false;
    let savePromises = [];

    for (const stepKey of Object.keys(this.state.steps ?? {})) {
      const step = this.state.steps?.[stepKey];

      if (step.queuedChanges.length === 0 && step.failedChanges.length === 0) {
        continue;
      }

      shouldRefetch = true;
      const saving = step.queuedChanges.concat(step.failedChanges);
      const excludeSaving = (xs: Date[]) => xs.filter(x => !saving.includes(x));

      // `stepKey` is what the server built: `rs.key`, plus `_<productUuid>`
      // when per-product. `rs.key` can itself contain "_" now (form-owned steps
      // are `f<formId>_<n>`), so splitting on "_" mis-reads it. Take the product
      // straight off the step instead, and strip its suffix to get the raw key:
      //
      //   "f4507_2"        + productId null   -> key "f4507_2", productId null
      //   "f4507_2_<uuid>" + productId <uuid> -> key "f4507_2", productId <uuid>
      const productId = step.step.productId ?? null;
      const key = productId ? stepKey.slice(0, -(productId.length + 1)) : stepKey;

      updateStep(stepKey, s => ({
        queuedChanges: excludeSaving(s.queuedChanges),
        savingChanges: s.savingChanges.concat(saving),
        failedChanges: excludeSaving(s.failedChanges),
      }));
      savePromises.push(
        saveStep({
          variables: {
            input: {
              stepKey: key,
              gigId: id,
              productId,
              state: step.state,
              stateChecksum: step.step.stateChecksum,
            },
          },
        })
          .then(async () => {
            updateStep(stepKey, s => ({
              savingChanges: excludeSaving(s.savingChanges),
            }));

            if (shouldRefetch) {
              await this.props.data.refetch();
            }

            this.setState({ saveState: resultingSaveState });
          })
          .catch(err => {
            resultingSaveState = "error";
            updateStep(stepKey, s => ({
              savingChanges: excludeSaving(s.savingChanges),
              failedChanges: s.failedChanges.concat(saving),
            }));

            throw err;
          }),
      );
    }
    await Promise.all(savePromises);
  };

  public debouncedSave = _.debounce(this.save, 1000);

  public handleSubmit = async () => {
    await this.save();

    const { submitReport, gig } = this.props;
    this.setState({ submitState: "submitting" });

    try {
      await submitReport({ variables: { gigId: gig.id } });

      this.setState({ submitState: null }, () => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
      });

      this.props.gigRefetch();
    } catch (err) {
      this.setState({ submitState: "error" });
      throw err;
    }
    if (this.state.homeAppUrl) {
      window.location.href = this.state.homeAppUrl;
    }
  };

  public formatDistance(checkStatus: CheckInOutStatus | undefined | null) {
    if (!checkStatus || checkStatus.latitude == null || checkStatus.longitude == null) {
      return "";
    }
    const { latitude, longitude } = this.props.gig.location ?? {};

    const distance = getDistance(
      checkStatus.latitude,
      checkStatus.longitude,
      latitude || 0,
      longitude || 0,
      earthRadiusMi,
    );

    return formatDistance(distance);
  }

  public formatTime(checkStatus: CheckInOutStatus) {
    return moment.tz(checkStatus.time, this.props.gig.timezone || "UTC").format("h:mma");
  }

  public formatCheckInSubtitle(isCheckedIn: boolean, gig?: Gig) {
    if (!gig) {
      return "";
    }

    if (CONFIG.steps.allowUpdatesBeforeCheckIn && !isCheckedIn) {
      return "Check in to finish report";
    }

    if (!isCheckedIn) {
      return "Check in to unlock report";
    }

    if (gig.programRole?.role?.skipReportingSteps === SkipReportingSteps.None) {
      const distance = this.formatDistance(gig.checkinStatus!);
      const time = this.formatTime(gig.checkinStatus!);

      return distance ? `${distance} miles away at ${time}` : `No location recorded at ${time}`;
    }

    return "";
  }

  public renderContent() {
    const {
      history,
      location,
      gig,
      gigRefetch,
      match: { isExact, url: checklistUrl, params },
      data,
      verifyReport,
      onSaveAndClose,
      userInfo,
    } = this.props;
    const { steps, submitState } = this.state;
    const checkStepsOn = gig?.programRole?.role?.hasCheckin ?? true;

    const returnToGo = () => {
      const orgId = (params as any).orgId;
      const goBackUrl = orgId ? getAppUrl("go", `${orgId}/tasks/${gig?.id}`) : getAppUrl("go");
      window.location.href = goBackUrl;
    };

    const goTo = (newStep: string) => () => {
      const { history } = this.props;
      window.scrollTo(0, 0);
      history.push({ ...location, pathname: `${checklistUrl}/${newStep}` });
    };

    const returnToChecklist = () => {
      history.push({ ...location, pathname: checklistUrl });
      gigRefetch();
    };

    const isCheckedIn = !!(gig && gig.checkinStatus);

    let stateText = "...";
    let allDone = false;
    let totalQuestions: number;
    let answered: number;

    if (data && data.gigReportSteps && steps && gig) {
      totalQuestions = checkStepsOn ? 2 : 0;
      answered = checkStepsOn ? (gig.checkinStatus ? 1 : 0) + (gig.checkoutStatus ? 1 : 0) : 0;

      for (const stepKey in steps) {
        if (steps.hasOwnProperty(stepKey)) {
          const { state } = steps[stepKey];

          for (const qId in state) {
            if (state.hasOwnProperty(qId)) {
              const answer = state[qId];
              if (answer.state !== "unasked") {
                totalQuestions++;

                if (answer.state !== "pending") {
                  answered++;
                }
              }
            }
          }
        }
      }

      if (totalQuestions > answered) {
        stateText = `${answered} of ${totalQuestions} answers`;
      } else {
        stateText = "All done!";
        allDone = true;
      }
    }

    const qSteps = (isDesktop: boolean) =>
      data &&
      steps &&
      data.gigReportSteps &&
      data.gigReportSteps
        .filter(s => steps[s.key]?.questions?.length > 0)
        .map(rs => {
          return (
            <>
              <StepItem
                key={rs.key}
                title={rs.name}
                subtitle={rs.shortDesc}
                progress={rs.progress}
                stepState={steps[rs.key]}
                isMultiStep={Object.keys(steps || {}).length > 1}
                onClick={async () => {
                  await this.debouncedSave();
                  goTo(rs.key)();
                }}
                testId={`gogetter.report.${rs.key}`}
                active={location.pathname.includes(rs.key)}
              >
                {verifyReport ? (
                  <ViewStep
                    handleSubmit={this.handleSubmit}
                    isDesktop={isDesktop}
                    allAnswered={totalQuestions > 0 && totalQuestions === answered}
                    verifyReport={verifyReport}
                    steps={steps}
                    data={data}
                    gig={gig}
                    returnToChecklist={returnToChecklist}
                    returnToGo={returnToGo}
                    checkStepsOn={checkStepsOn}
                    gigRefetch={gigRefetch}
                    handleAnswerChanged={this.handleAnswerChanged}
                    save={this.save}
                    activeStepKey={rs.key}
                    setExpensesDirty={expensesDirty => this.setState(p => ({ ...p, expensesDirty }))}
                    expensesDirty={this.state.expensesDirty}
                    userInfo={userInfo}
                  />
                ) : null}
              </StepItem>
            </>
          );
        });

    const isCheckedOut = gig && !!gig.checkoutStatus;

    const hasExpenses = gig?.expenses?.length > 0;
    const talentExpensesEnabled = gig?.talentExpenses;

    let checkOutTitle = isCheckedOut ? "Checked out!" : "Check out";

    return (
      <Desktop>
        {isDesktop => {
          return (
            <div className={cx(styles.reportColumns, verifyReport && styles.verifyReportColumns)}>
              {(isExact || isDesktop) && (
                <ListView className={cx(isDesktop && !verifyReport && styles.stepList)}>
                  {!verifyReport && checkStepsOn && (
                    <StepItem
                      active={location.pathname.includes("/checkIn")}
                      title={isCheckedIn ? "Checked in!" : "Check in"}
                      subtitle={this.formatCheckInSubtitle(isCheckedIn, gig)}
                      progress={isCheckedIn ? 100 : 0}
                      onClick={isCheckedIn ? undefined : goTo("checkIn")}
                      red={!isCheckedIn}
                      testId="gogetter.report.checkIn"
                    />
                  )}

                  {qSteps(isDesktop) || (
                    <LoadingContainer message={"Loading report steps..."} center className={styles.loadingSteps} />
                  )}

                  {gig?.programRole?.allowExpenses && isPinata && (
                    <StepItem
                      title="Expenses"
                      key="expense"
                      progress={(hasExpenses || !talentExpensesEnabled) && !this.state.expensesDirty ? 100 : 0}
                      onClick={goTo("expenses")}
                      active={location.pathname.includes("/expenses")}
                    />
                  )}

                  {!verifyReport && checkStepsOn && (
                    <StepItem
                      active={location.pathname.includes("/checkOut")}
                      title={checkOutTitle}
                      subtitle={
                        isCheckedOut
                          ? gig?.programRole?.role?.skipReportingSteps === SkipReportingSteps.None
                            ? this.formatDistance(gig.checkoutStatus)
                              ? `${this.formatDistance(
                                  gig.checkoutStatus,
                                )} miles from task location at ${this.formatTime(gig.checkoutStatus!)}`
                              : `No location recorded at ${this.formatTime(gig.checkoutStatus!)}`
                            : ""
                          : "Do this when you're leaving the task venue"
                      }
                      progress={isCheckedOut ? 100 : 0}
                      onClick={goTo("checkOut")}
                      testId="gogetter.report.checkOut"
                    />
                  )}
                </ListView>
              )}

              <ReportFooter
                verifyReport={verifyReport}
                allDone={allDone}
                steps={steps}
                gig={gig}
                returnToChecklist={returnToChecklist}
                checkStepsOn={checkStepsOn}
                gigRefetch={gigRefetch}
                handleSubmit={this.handleSubmit}
                onSaveAndClose={onSaveAndClose}
                state={submitState || "idle"}
                isCheckedIn={isCheckedIn}
                submitState={submitState || "idle"}
                save={this.save}
                reportHasUnsavedChanges={this.reportHasUnsavedChanges}
                activeStepKey={this.getActiveStepKey()}
                checklistUrl={checklistUrl}
                data={data}
                showOnCheckIn={CONFIG.footer.showOnCheckIn}
              />

              {!verifyReport ? (
                <ViewStep
                  isDesktop={isDesktop}
                  handleSubmit={this.handleSubmit}
                  allAnswered={totalQuestions > 0 && totalQuestions === answered}
                  verifyReport={verifyReport}
                  steps={steps}
                  data={data}
                  gig={gig}
                  returnToChecklist={returnToChecklist}
                  returnToGo={returnToGo}
                  checkStepsOn={checkStepsOn}
                  gigRefetch={gigRefetch}
                  handleAnswerChanged={this.handleAnswerChanged}
                  save={this.save}
                  setExpensesDirty={expensesDirty => this.setState(p => ({ ...p, expensesDirty }))}
                  expensesDirty={this.state.expensesDirty}
                  userInfo={userInfo}
                />
              ) : null}
            </div>
          );
        }}
      </Desktop>
    );
  }

  public async fetchHomeApp(orgId: string) {
    try {
      const url = `${RUSTEND_URL}/organizations/${orgId}/organization_users/home_app`;

      const homeAppResponse = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwt()}`,
          "X-Pinata-Organization-Id": orgId,
        },
      });

      const homeApp = await homeAppResponse.json();
      this.setState(state => ({ ...state, homeAppUrl: getUrl(homeApp, orgId) }));
    } catch (error) {
      console.error("Error fetching home app:", error);
    }
  }

  public render() {
    const { anyError } = this.state;
    const { verifyReport, agencyName, clientName, logoUrl, gig } = this.props;

    return (
      <>
        {verifyReport ? null : (
          <ReportHeader
            agencyName={agencyName}
            clientName={clientName}
            logoUrl={logoUrl}
            gig={gig}
            onSave={this.save}
          />
        )}

        <div className={cx(styles.fieldReport, verifyReport && styles.noPaddingReport)}>
          <div className={anyError ? styles.visibleErrorBanner : styles.errorBanner}>
            <Text.Display5 kind={"reverse"} className={styles.errorTitle}>
              Your report has not been saved.
            </Text.Display5>
            <Text.P4 kind={"reverse"}>
              Keep working — changes are stored temporarily on your device. Save again when you have better service.
            </Text.P4>
          </div>

          {this.renderContent()}
        </div>
        <div className={styles.iOSReport} />
      </>
    );
  }
}

function getUrl(homeApp: { home_app: string }, orgId: string) {
  const appMap: Record<string, string> = {
    TheApp: "theApp",
    Dashboard: "dashboard",
    Go: "go",
  };

  const redirectToApp = (appMap[homeApp.home_app] || "dashboard") as AppName;

  return `${getBaseAppUrl(redirectToApp)}/${orgId}`;
}

interface ViewStep {
  isDesktop: boolean;
  verifyReport: boolean | undefined;
  steps: StepStates | null;
  data: Data;
  gig: Gig;
  userInfo: UserInfo;
  returnToChecklist: () => void;
  returnToGo: () => void;
  checkStepsOn: boolean;
  gigRefetch: () => Promise<void>;
  handleAnswerChanged: (stepKey: string) => (qId: string, answer: GigReportAnswer<{}>, debounce: boolean) => void;
  save: () => Promise<void>;
  activeStepKey?: string;
  setExpensesDirty: (dirty: boolean) => void;
  expensesDirty: boolean;
  allAnswered: boolean;
  handleSubmit: () => Promise<void>;
}

function ViewStep(props: ViewStep) {
  const {
    isDesktop,
    verifyReport,
    steps,
    data,
    gig,
    returnToChecklist,
    returnToGo,
    checkStepsOn,
    gigRefetch,
    handleAnswerChanged,
    save,
    activeStepKey,
    setExpensesDirty,
    expensesDirty,
    allAnswered,
    userInfo,
  } = props;
  const {
    history,
    match: { path, isExact, url: checklistUrl },
    location,
  } = useRouter();

  const isTalentOrg = userInfo.me?.organizationUser.organizationId === gig.talentOrganization?.id;
  const onCancel = isTalentOrg ? returnToGo : undefined;

  return (
    <div className={cx(isDesktop && !verifyReport && styles.stepRender, !isExact && styles.flexStep)}>
      {!steps || !data || !data.gigReportSteps || !gig ? (
        <LoadingContainer message={"Loading report steps..."} center className={styles.loadingSteps} />
      ) : (
        <Switch>
          <Route
            exact
            path={path + "/"}
            render={() =>
              isDesktop && !verifyReport ? (
                gig.checkinStatus || !checkStepsOn ? (
                  <div className={styles.defaultStep}>
                    <Text.Display2>Select a report step on the left</Text.Display2>
                    <Text.P4>Remember to submit your report when you're finished!</Text.P4>
                  </div>
                ) : (
                  <CheckInOut
                    gig={gig}
                    returnToChecklist={returnToChecklist}
                    showHeader={isDesktop}
                    onCancel={onCancel}
                  />
                )
              ) : null
            }
          />

          {gig && checkStepsOn && (
            <Route
              path={path + "/checkOut"}
              render={() => (
                <CheckInOut
                  gig={gig}
                  returnToChecklist={returnToChecklist}
                  onCancel={onCancel}
                  checkout
                  showHeader={isDesktop}
                />
              )}
            />
          )}

          {gig && checkStepsOn && (
            <Route
              path={path + "/checkIn"}
              render={() => (
                <CheckInOut
                  gig={gig}
                  returnToChecklist={returnToChecklist}
                  onCancel={onCancel}
                  showHeader={isDesktop}
                />
              )}
            />
          )}

          {gig.programRole?.allowExpenses && (
            <Route
              path={path + "/expenses"}
              render={() => (
                <Expenses
                  gigId={gig.id}
                  expensesDirty={expensesDirty}
                  setExpensesDirty={setExpensesDirty}
                  returnToChecklist={returnToChecklist}
                  gigRefetch={gigRefetch}
                />
              )}
            />
          )}

          <Redirect
            path={path + "/startStep"}
            to={{
              ...location,
              pathname: checklistUrl + (gig && checkStepsOn ? "/checkIn" : "/" + data.gigReportSteps?.[0]?.key),
            }}
          />

          <Route
            path={`${path}/:stepKey`}
            render={({
              match: {
                params: { stepKey },
              },
            }) => {
              const gigReportStep = data.gigReportSteps.find(rs => rs.key === stepKey);
              const stepState = steps && steps[stepKey || ""];

              if (!gigReportStep || !stepState || !stepKey || !stepState.questions) {
                return <div>Step not found</div>;
              }
              if (activeStepKey !== stepKey && isDesktop && verifyReport) {
                return null;
              }

              return (
                <>
                  <StepForm
                    gig={gig}
                    step={stepState}
                    disabled={
                      CONFIG.steps.allowUpdatesBeforeCheckIn
                        ? false
                        : !gig.checkinStatus && !verifyReport && checkStepsOn
                    }
                    setAnswer={handleAnswerChanged(stepKey)}
                    returnToChecklist={async (goToNext: boolean = false) => {
                      try {
                        await save();
                      } catch (e) {
                        console.error(e);
                      } finally {
                        const nextStep = getNextStep(data, stepKey, gig);
                        const nextUrl = goToNext && nextStep ? `${checklistUrl}/${nextStep.key}` : checklistUrl;
                        history.push({ ...location, pathname: nextUrl });
                        if (nextStep && goToNext) {
                          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                        }
                      }
                    }}
                    verifyReport={verifyReport}
                    goToCheckin={() => history.push({ ...location, pathname: `checkIn` })}
                    isMobile={!isDesktop}
                    allAnswered={allAnswered}
                  />
                </>
              );
            }}
          />
        </Switch>
      )}
    </div>
  );
}

const Default: React.ComponentType<ParentProps> = compose(
  graphql(saveStep, {
    props: ({ mutate }) => ({
      saveStep: mutate,
    }),
  }),
  graphql<any, any, any, any>(submitReport, {
    props: ({ mutate }) => {
      return {
        submitReport: mutate,
      };
    },
  }),

  graphql<any, any, any, any>(reportSteps, {
    skip: ({ gig }: Props) => !gig,
    options: ({ gig, gigId }: Props & { gigId?: string }) => ({
      variables: {
        gigId: gig ? gig.id : gigId,
      },

      fetchPolicy: "cache-and-network",
    }),
  }),
)(Report);

export default Default;

interface ReportFooterNoteProps {
  allDone: boolean;
  currentStepComplete: boolean;
}

export function ReportFooterNote(props: ReportFooterNoteProps) {
  return props.allDone ? (
    <Text.H4 kind="reverse" className={styles.footerNoteGreen}>
      Nice Job!
      <span>&nbsp; You've provided all required info.</span>
    </Text.H4>
  ) : props.currentStepComplete ? (
    <Text.H4 kind="reverse" className={styles.footerNoteGreen}>
      All set here!
      <span>&nbsp;Head to the next page</span>
    </Text.H4>
  ) : (
    <Text.H4 kind="reverse" className={styles.footerNote}>
      More info is needed.
      <span>&nbsp; Don't forget to scroll down.</span>
    </Text.H4>
  );
}

interface GoToListProps {
  onClick: () => void;
}

function GoToList(props: GoToListProps) {
  return (
    <Button
      kind="translucent"
      className={styles.secondaryFooterAction}
      onClick={props.onClick}
      data-test={`go-to-list`}
    >
      <Icon src={clipboardIcon} fill={"#cde1fd"} size={26} />
    </Button>
  );
}

interface ReportFooterProps {
  verifyReport: boolean | undefined;
  allDone: boolean;
  steps: StepStates | null;
  gig: Gig;
  returnToChecklist: () => void;
  checkStepsOn: boolean;
  gigRefetch: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  onSaveAndClose?: () => void;
  state: string;
  isCheckedIn: boolean;
  submitState: string;
  save: () => void;
  reportHasUnsavedChanges: () => boolean;
  activeStepKey?: string;
  checklistUrl: string;
  data: Data;
  showOnCheckIn: boolean;
}

function isCurrentStepComplete(stepState?: StepState): boolean {
  if (!stepState) return false;
  return Object.values(stepState.state).every(question => {
    return question.state !== "pending";
  });
}

function ReportFooter({
  verifyReport,
  allDone,
  steps,
  gig,
  returnToChecklist,
  checkStepsOn,
  handleSubmit,
  onSaveAndClose,
  state,
  submitState,
  reportHasUnsavedChanges,
  activeStepKey,
  checklistUrl,
  data,
  save,
  showOnCheckIn,
}: ReportFooterProps) {
  const { history, location } = useRouter();

  const currentStepComplete = activeStepKey && steps ? isCurrentStepComplete(steps[activeStepKey]) : false;

  const noActiveStep = !activeStepKey;
  const noCheckoutStatus = !gig?.checkoutStatus;
  const onCheckout = location.pathname.includes("checkOut") && noCheckoutStatus;
  const onCheckin = location.pathname.includes("checkIn") && !showOnCheckIn;

  if ((noCheckoutStatus && noActiveStep) || onCheckout || onCheckin) {
    return null;
  }

  const handleFooterSubmit = async () => {
    save();

    if (activeStepKey && steps && currentStepComplete) {
      const nextStep = getNextStep(data, activeStepKey, gig);
      if (nextStep) {
        const nextUrl = `${checklistUrl}/${nextStep.key}`;
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        history.push({ ...location, pathname: nextUrl });
      }
    }
  };

  if (verifyReport) {
    return onSaveAndClose ? (
      <ActionFooter
        className={styles.actionFooter}
        title={activeStepKey ? "Save & return to list" : "Save & Close"}
        disabled={state === "saving"}
        onAction={async () => {
          if (activeStepKey) {
            save();
            returnToChecklist();
          } else {
            onSaveAndClose();
          }
        }}
      />
    ) : null;
  }

  return (
    <FooterDistance distance={0}>
      {gig && !FINAL_REPORT_STATES.includes(gig.workflowStateKey) ? (
        <ActionFooter
          title={
            !allDone
              ? currentStepComplete
                ? "Save & Continue"
                : "Save my progress"
              : gig.workflowStateKey === "reportDraft" || (gig.workflowStateKey === "upcoming" && !checkStepsOn)
              ? "Complete & Close"
              : "Update my report"
          }
          note={<ReportFooterNote allDone={allDone} currentStepComplete={currentStepComplete} />}
          errorMessage={
            submitState === "error" ? (
              <Text.Message kind={"error"} className={styles.submitError}>
                We couldn't submit your report. Try again later.
              </Text.Message>
            ) : null
          }
          disabled={submitState === "submitting" || (!allDone && !reportHasUnsavedChanges())}
          secondaryAction={Object.keys(steps || {}).length > 1 ? <GoToList onClick={returnToChecklist} /> : null}
          onAction={!allDone && reportHasUnsavedChanges() ? handleFooterSubmit : handleSubmit}
          testId={"gogetter.report.actionButton"}
        />
      ) : null}
    </FooterDistance>
  );
}
